import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@shared/auth/AuthContext';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';
import { consumePendingMsalRedirectResult } from '../config/msalConfig';
import { apiClient, setCsrfToken } from '@shared/services/ApiClient';

export default function MicrosoftCallbackPage() {
  const { loginWithMicrosoft } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Consume the redirect result captured in main.tsx during initializeApp().
        // This avoids calling handleRedirectPromise() again from the component, which
        // can return null if MSAL's internal cache key changed after the URL was cleared.
        const result = consumePendingMsalRedirectResult();

        if (!result?.accessToken) {
          // No pending redirect result — user navigated here directly
          navigate('/login', { replace: true });
          return;
        }

        // --- Invitation acceptance path ---
        const invitationToken = sessionStorage.getItem('invitation_token');
        if (invitationToken) {
          sessionStorage.removeItem('invitation_token');
          const response = await apiClient.post<any>('/auth/accept-invitation/microsoft', {
            token: invitationToken,
            accessToken: result.accessToken
          });
          if (response.csrfToken) setCsrfToken(response.csrfToken);
          // Redirect based on role (full reload so auth context picks up the new cookies)
          const role = response.user?.role;
          if (role === 'Admin') window.location.replace('/admin/dashboard');
          else if (role === 'Agent') window.location.replace('/agent/dashboard');
          else window.location.replace('/customer/dashboard');
          return;
        }

        // --- Normal sign-up / sign-in path ---
        const companyName = sessionStorage.getItem('microsoft_signup_company') || undefined;
        const agreeToTerms = sessionStorage.getItem('microsoft_signup_agreeToTerms') === 'true';
        const marketingConsent = sessionStorage.getItem('microsoft_signup_marketingConsent') === 'true';
        sessionStorage.removeItem('microsoft_signup_company');
        sessionStorage.removeItem('microsoft_signup_agreeToTerms');
        sessionStorage.removeItem('microsoft_signup_marketingConsent');

        // Exchange Microsoft access token for our app session (sets HttpOnly cookies)
        const authResult = await loginWithMicrosoft(result.accessToken, companyName, agreeToTerms, marketingConsent);

        if (authResult.success) {
          // Navigate within the SPA so auth context state (isAuthenticated, user) is
          // preserved. window.location.replace('/') was causing a full reload which
          // re-ran initializeAuth — and since '/' is a public path it skipped the
          // /auth/me check, leaving isAuthenticated=false on the landing page.
          navigate('/', { replace: true });
        } else {
          setError(authResult.message);
        }
      } catch (err: any) {
        console.error('[MicrosoftCallback] error:', err);
        setError(err.message || 'Microsoft login failed. Please try again.');
      }
    };

    processCallback();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 2 }}>
        <Alert severity="error" sx={{ maxWidth: 400 }}>{error}</Alert>
        <Typography component="a" href="/login" sx={{ color: 'primary.main' }}>
          Back to login
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 2 }}>
      <CircularProgress />
      <Typography color="text.secondary">Completing Microsoft sign-in...</Typography>
    </Box>
  );
}
